import type { SyntheticEvent } from 'react';

/**
 * Hands an image's intrinsic aspect ratio to its stylesheet as `--img-ratio`, once the bitmap
 * has loaded. CSS cannot read a replaced element's intrinsic ratio, and the sizing rule the
 * transcript wants — capped at 60vh, never narrower than two-thirds of the column, aspect ratio
 * always held — needs it: the width that a height cap implies is `60vh × ratio`, and clamping
 * that between the two width bounds is what keeps all three promises at once. `min-width` and
 * `max-height` together would not: the CSS sizing table resolves that conflict by honouring
 * both and distorting the bitmap.
 *
 * The same load also publishes the bitmap's size in CSS pixels as `--img-native-w` /
 * `--img-native-h`, which is the ceiling every surface caps its image at so a small file is
 * never upscaled into blur.
 */
export function publishIntrinsicRatio(event: SyntheticEvent<HTMLImageElement>) {
  const img = event.currentTarget;
  if (img.naturalWidth <= 0 || img.naturalHeight <= 0) return;

  img.style.setProperty('--img-ratio', String(img.naturalWidth / img.naturalHeight));

  /**
   * The bitmap's own size, in CSS pixels — its natural size divided by the device pixel ratio,
   * which is how many device pixels one CSS pixel is drawn with. A 1230px-wide PNG is 615 CSS px
   * on a 2x display, and stretching it to 1230 CSS px asks the screen for 2460 device pixels
   * from a 1230-pixel file: the blur a reader sees on a chart that is *already* retina-sharp at
   * half the width.
   *
   * Published rather than applied, because the ceiling is only one of the bounds each surface
   * has — the transcript's image is also bounded by the column and by 60vh, the viewer's by the
   * slide — and CSS composes them with `min()` far better than a resize handler could.
   */
  const density = window.devicePixelRatio || 1;
  img.style.setProperty('--img-native-w', `${img.naturalWidth / density}px`);
  img.style.setProperty('--img-native-h', `${img.naturalHeight / density}px`);
}
